/**
 * Funnel step 3 (pet photo). Validates type/size, shows an instant preview,
 * stores the actual File in IndexedDB (funnel-photo-db.js — never in
 * sessionStorage), and merges only small metadata + the optional notes into
 * the shared funnel state (funnel-state.js), which already holds
 * email/acceptsMarketing/petName from the previous steps.
 *
 * Large mobile photos (iPhone/Android originals are routinely well over
 * 8 MB) are compressed client-side, automatically, before being stored —
 * the customer never has to do this themselves. Hard reject only kicks in
 * above HARD_MAX_MB (a genuinely too-large or corrupt file); anything at
 * or under `maxSizeMb` (the backend-safe threshold, from the section
 * setting) is stored completely untouched.
 */
(() => {
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  // Absolute ceiling: above this, we don't even try to compress — the file
  // is either not really a photo or unreasonably large for a pet picture.
  const HARD_MAX_MB = 30;
  const HARD_MAX_BYTES = HARD_MAX_MB * 1024 * 1024;

  // Longest-side cap for compressed output, and the JPEG quality ladder
  // tried (high quality first — this photo is used to identify the pet
  // precisely, so we only drop quality as far as actually needed).
  const MAX_DIMENSION = 2800;
  const JPEG_QUALITY_STEPS = [0.92, 0.88, 0.8, 0.72, 0.62, 0.5];

  document.querySelectorAll('[data-pet-photo-form]').forEach(async (form) => {
    const dropzone = form.querySelector('[data-pet-photo-dropzone]');
    const fileInput = form.querySelector('[data-pet-photo-input]');
    const emptyState = form.querySelector('[data-pet-photo-empty]');
    const previewState = form.querySelector('[data-pet-photo-preview]');
    const previewImage = form.querySelector('[data-pet-photo-preview-image]');
    const changeButton = form.querySelector('[data-pet-photo-change]');
    const errorEl = form.querySelector('[data-pet-photo-error]');
    const notesInput = form.querySelector('[data-pet-photo-notes]');
    const notesLabel = form.querySelector('[data-pet-photo-notes-label]');
    const submitBtn = form.querySelector('[data-pet-photo-submit]');
    const buttonLabel = form.querySelector('[data-pet-photo-button-label]');
    const nextUrl = form.dataset.nextUrl || '/pages/personalizar-figurita';
    // Backend-safe threshold: files at or under this are left untouched;
    // above it (and up to HARD_MAX_BYTES), the photo is auto-compressed to
    // land back under this same size.
    const maxSizeMb = parseFloat(form.dataset.maxSize) || 8;
    const maxSizeBytes = maxSizeMb * 1024 * 1024;

    const state = window.PfFunnel ? window.PfFunnel.get() : {};
    let currentPreviewUrl = null;
    let hasPhoto = false;

    // Personalize the notes label with the pet's name from step 2, without
    // ever leaving the raw [PET_NAME] token visible if it's missing.
    if (notesLabel) {
      const petName = state.petName || 'ta mascotte';
      notesLabel.textContent = notesLabel.textContent.replace('[PET_NAME]', petName);
    }

    if (notesInput && state.petNotes) {
      notesInput.value = state.petNotes;
    }
    notesInput?.addEventListener('input', () => {
      window.PfFunnel.set({ petNotes: notesInput.value });
    });

    const showError = (message) => {
      errorEl.classList.remove('is-processing');
      errorEl.textContent = message;
      errorEl.hidden = false;
    };

    const clearError = () => {
      errorEl.classList.remove('is-processing');
      errorEl.hidden = true;
      errorEl.textContent = '';
    };

    // Reuses the same status slot as errors (always visible, in both the
    // empty and preview dropzone states) but in a neutral tone — this is a
    // real in-progress status, not a problem.
    const showProcessing = (message) => {
      errorEl.classList.add('is-processing');
      errorEl.textContent = message;
      errorEl.hidden = false;
    };

    const clearProcessing = () => {
      errorEl.classList.remove('is-processing');
      errorEl.hidden = true;
      errorEl.textContent = '';
    };

    const showPreview = (file) => {
      if (currentPreviewUrl) URL.revokeObjectURL(currentPreviewUrl);
      currentPreviewUrl = URL.createObjectURL(file);
      previewImage.src = currentPreviewUrl;
      emptyState.hidden = true;
      previewState.hidden = false;
      dropzone.classList.add('has-photo');
      changeButton.hidden = false;
      hasPhoto = true;
      submitBtn.disabled = false;
    };

    const clearPhotoUi = () => {
      emptyState.hidden = false;
      previewState.hidden = true;
      dropzone.classList.remove('has-photo');
      changeButton.hidden = true;
      hasPhoto = false;
      submitBtn.disabled = true;
    };

    const validateFile = (file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        return 'Format non pris en charge. Utilise une image JPG, PNG ou WEBP.';
      }
      if (file.size > HARD_MAX_BYTES) {
        return `Cette photo est trop lourde (max. ${HARD_MAX_MB} Mo).`;
      }
      return null;
    };

    // --- Client-side compression (only runs when file.size > maxSizeBytes) ---

    async function loadImageSource(file) {
      // imageOrientation: 'from-image' bakes in the EXIF rotation so the
      // canvas below never has to parse EXIF by hand.
      if ('createImageBitmap' in window) {
        try {
          return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (e) {
          // Some browsers can't decode this particular file via
          // createImageBitmap — fall back to a plain <img>.
        }
      }
      const objectUrl = URL.createObjectURL(file);
      try {
        return await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('image_load_failed'));
          img.src = objectUrl;
        });
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }

    function drawToCanvas(source, maxDimension) {
      const width = source.width || source.naturalWidth;
      const height = source.height || source.naturalHeight;
      // Never upscale — only shrink photos that are actually larger than
      // the target, keeping the original aspect ratio exactly.
      const scale = Math.min(1, maxDimension / Math.max(width, height));
      const targetWidth = Math.max(1, Math.round(width * scale));
      const targetHeight = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      canvas.getContext('2d').drawImage(source, 0, 0, targetWidth, targetHeight);
      return canvas;
    }

    function canvasToJpegBlob(canvas, quality) {
      return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    }

    /**
     * Compresses a large photo entirely client-side so it fits back under
     * maxSizeBytes (the backend-safe threshold): downsizes to at most
     * MAX_DIMENSION px on the longest side, then exports as JPEG, trying
     * progressively lower quality ONLY as far as actually needed — quality
     * and resolution are never reduced more than necessary, since this
     * photo is what's used to identify the pet precisely.
     */
    async function compressPhoto(file) {
      const source = await loadImageSource(file);
      const canvas = drawToCanvas(source, MAX_DIMENSION);
      if (typeof source.close === 'function') source.close();

      let lastBlob = null;
      for (const quality of JPEG_QUALITY_STEPS) {
        const blob = await canvasToJpegBlob(canvas, quality);
        if (!blob) continue;
        lastBlob = blob;
        if (blob.size <= maxSizeBytes) break;
      }

      if (!lastBlob) throw new Error('compression_failed');

      const newName = file.name.replace(/\.[^./\\]+$/, '') + '.jpg';
      return new File([lastBlob], newName, { type: 'image/jpeg' });
    }

    const handleFile = async (file) => {
      if (!file) return;

      const error = validateFile(file);
      if (error) {
        showError(error);
        return;
      }

      clearError();

      let photoToStore = file;

      if (file.size > maxSizeBytes) {
        showProcessing('Préparation de ta photo…');
        try {
          photoToStore = await compressPhoto(file);
        } catch (e) {
          clearProcessing();
          showError("Cette photo n'a pas pu être traitée. Réessaie ou choisis une autre photo.");
          return;
        }
        clearProcessing();
      }

      showPreview(photoToStore);

      try {
        await window.PfFunnelPhotoDB.savePhoto(photoToStore);
        window.PfFunnel.set({
          petPhotoMeta: { name: photoToStore.name, size: photoToStore.size, type: photoToStore.type },
        });
      } catch (e) {
        showError("La photo n'a pas pu être conservée sur cet appareil. Réessaie ou choisis une autre photo.");
        clearPhotoUi();
      }
    };

    dropzone.addEventListener('click', () => fileInput.click());
    dropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        fileInput.click();
      }
    });

    fileInput.addEventListener('change', () => {
      handleFile(fileInput.files[0]);
    });

    changeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      fileInput.value = '';
      fileInput.click();
    });

    ['dragover', 'dragenter'].forEach((type) => {
      dropzone.addEventListener(type, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'dragend'].forEach((type) => {
      dropzone.addEventListener(type, () => {
        dropzone.classList.remove('is-dragover');
      });
    });

    dropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      dropzone.classList.remove('is-dragover');
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) handleFile(file);
    });

    // Restore a photo already picked earlier in this session (e.g. the user
    // went back to step 2 and returned here).
    if (state.petPhotoMeta) {
      try {
        const storedFile = await window.PfFunnelPhotoDB.getPhoto();
        if (storedFile) {
          showPreview(storedFile);
        }
      } catch (e) {
        // No stored photo available (different device/browser, cleared
        // storage, etc.) — leave the dropzone empty, user re-selects.
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (!hasPhoto) {
        showError('Merci de choisir une photo avant de continuer.');
        return;
      }

      clearError();
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      if (buttonLabel) buttonLabel.textContent = 'Un instant…';

      window.location.href = nextUrl;
    });
  });
})();
